<template>
  <div>
        <!-- Chart Container -->
        <div style="position: relative; height: 320px;">
          <canvas ref="historyChart" aria-label="Inventory History Chart" role="img"></canvas>
        </div>
        <!-- /Chart Container -->
        
        <!-- Load More Button -->
        <div v-if="hasMore" class="text-center" style="margin-top:0.5rem">
          <button class="btn btn-sm btn-outline-primary" @click="loadMore" :disabled="loadingMore">
            <span v-if="!loadingMore">Load more</span>
            <span v-else>Loading...</span>
          </button>
        </div>
        
        <!-- Loading & Pagination -->
        <table-loading-bar v-if="loading" class="text-center" />
        <div v-if="loading === false && logs.count > logs.data.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
            <compact-pagination :pages="pagination" :current-page="page" @change="getInventoriesLog" />
        </div>
    </div>
</template>


<script>
import helper from '../helper'
import Chart from 'chart.js'
export default {
  name: 'InventoryHistoryComponent',
  props : ["inventory_id","product_id"],
  data(){
    return {
        logs : [],
        start : null,
        end : null,
        loading :false,
        loadingMore: false,
        per_page : 25,
        page : 1,
        count : 0,
        pagination : [],
        chart: null,
        chartMeta: {
          actions: [],
          quantities: [],
          dates: []
        }
    }
  },
  created(){
    this.getInventoriesLog()
  },
  computed: {
    hasMore(){
      const total = this.count || (this.logs && this.logs.count) || 0
      const currentLength = (this.logs && this.logs.data) ? this.logs.data.length : 0
      return currentLength < total
    }
  },
  beforeDestroy(){
    if(this.chart){
      this.chart.destroy()
      this.chart = null
    }
  },
  methods : {
    formatHumanDate(number) { return helper.formatDate(number,true) },
    async getInventoriesLog(page){
      this.page = page ? page : 1
      this.loading = true
      this.logs = []
      const inventoryHistory = await helper.customGet(`${process.env.VUE_APP_API}/inventories-history?per_page=${this.per_page}&page=${page}&product_id=${this.product_id}&inventory_id=${this.inventory_id}`)
      this.logs = inventoryHistory.data
      this.count = inventoryHistory.count

      //pagination
      this.pagination = []
      let MaxPagination = Math.ceil(this.logs.count / this.per_page )
      for(let i = 1 ; i <= MaxPagination ;i++){
          this.pagination.push(i)
      }

      this.loading = false
      this.$nextTick(() => {
        this.buildAndRenderChart()
      })
    },
    async loadMore(){
      if (this.loading || this.loadingMore) return
      const total = this.count || (this.logs && this.logs.count) || 0
      const currentLength = (this.logs && this.logs.data) ? this.logs.data.length : 0
      if (currentLength >= total) return
      const nextPage = (this.page || 1) + 1
      this.loadingMore = true
      try {
        const inventoryHistory = await helper.customGet(`${process.env.VUE_APP_API}/inventories-history?per_page=${this.per_page}&page=${nextPage}&product_id=${this.product_id}&inventory_id=${this.inventory_id}`)
        // Append new data to existing logs
        if (inventoryHistory && inventoryHistory.data) {
          if (Array.isArray(inventoryHistory.data.data)) {
            // Standard shape: { data: [...], count }
            if (!this.logs || !Array.isArray(this.logs.data)) {
              this.logs = { data: [], count: 0 }
            }
            this.logs.data = this.logs.data.concat(inventoryHistory.data.data)
            // refresh total count if provided
            if (typeof inventoryHistory.count === 'number') this.count = inventoryHistory.count
            else if (typeof inventoryHistory.data.count === 'number') this.count = inventoryHistory.data.count
          } else if (Array.isArray(inventoryHistory.data)) {
            // Fallback shape: data is an array
            if (!this.logs || !Array.isArray(this.logs.data)) {
              this.logs = { data: [], count: this.count || 0 }
            }
            this.logs.data = this.logs.data.concat(inventoryHistory.data)
          }
        }
        this.page = nextPage
        this.$nextTick(() => this.buildAndRenderChart())
      } finally {
        this.loadingMore = false
      }
    },
    buildAndRenderChart(){
      // Prepare data arrays
      const dates = (this.logs.data || []).map(l => this.formatHumanDate(l.created_at))
      // Compact labels for X axis to avoid clutter (DD/MM or fallback)
      const shortLabels = (this.logs.data || []).map(l => {
        const d = new Date(l.created_at)
        if (!isNaN(d)) {
          const dd = String(d.getDate()).padStart(2, '0')
          const mm = String(d.getMonth() + 1).padStart(2, '0')
          return `${dd}/${mm}`
        }
        const s = String(l.created_at || '')
        return s.length >= 10 ? s.slice(5, 10).replace('-', '/') : s
      })
      const quantities = (this.logs.data || []).map(l => {
        const n = Number(l.quantity)
        return Number.isFinite(n) ? Math.round(n) : 0
      })
      const actions = (this.logs.data || []).map(l => l.action)

      this.chartMeta = { actions, quantities, dates }

      const ctx = this.$refs.historyChart
      if(!ctx){ return }

      // Destroy previous chart if any
      if(this.chart){
        this.chart.destroy()
        this.chart = null
      }

      // Determine a sampling step for x-axis labels (~8 labels max)
      const maxXTicks = 8
      const step = Math.max(1, Math.ceil(shortLabels.length / maxXTicks))

      // Create line chart
      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: shortLabels,
          datasets: [{
            label: this.$t('current_quantity'),
            data: quantities,
            fill: false,
            borderColor: '#534dff',
            backgroundColor: 'rgba(83,77,255,0.25)',
            pointBackgroundColor: '#534dff',
            pointBorderColor: '#ffffff',
            pointRadius: 4,
            pointHoverRadius: 6,
            lineTension: 0.2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          tooltips: {
            mode: 'nearest',
            intersect: false,
            callbacks: {
              // Show translated action and quantity in tooltip
              label: (tooltipItem) => {
                const idx = tooltipItem.index
                const act = this.chartMeta.actions[idx]
                const qty = this.chartMeta.quantities[idx]
                return `${this.$t(act)}: ${qty}`
              },
              title: (tooltipItems) => {
                const idx = tooltipItems[0].index
                return this.chartMeta.dates[idx]
              }
            }
          },
          scales: {
            xAxes: [{
              display: true,
              scaleLabel: { display: true, labelString: this.$t('date') },
              ticks: {
                autoSkip: false,
                maxTicksLimit: 8,
                maxRotation: 0,
                minRotation: 0,
                callback: (value, index) => {
                  // Only show every Nth label to avoid clutter
                  return index % step === 0 ? value : ''
                }
              }
            }],
            yAxes: [{
              display: true,
              scaleLabel: { display: true, labelString: this.$t('current_quantity') },
              ticks: {
                beginAtZero: true,
                stepSize: 1,
                callback: (value) => {
                  // Only show integer tick labels
                  return Number.isInteger(value) ? value : ''
                }
              }
            }]
          },
          elements: {
            point: {
              hoverBorderWidth: 2
            }
          }
        }
      })
    }
  }
}
</script>
